import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import styles from "../styles/AddGifteeWizard.module.css";
import { reflexivePronoun, subjectPronoun } from "../utils/gifteePronouns.js";
import GifteeBudgetRangeSlider from "../components/GifteeBudgetRangeSlider.jsx";
import { db } from "../../../firebase.js";
import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { useAuth } from "../../../hooks/useAuth";
import {
  getGifteeHubPath,
  getWizardReturnPath,
  gifteeHubAfterCreateOptions,
} from "../createGifteeWizardNav.js";
import { INTEREST_GROUPS } from "../data/interestGroups.js";
import { customInterestsFromSet } from "../utils/gifteeInterests.js";
import {
  getNextUsHolidayOccurrence,
  getUsHolidayOccasions,
} from "../data/usHolidayPresets.js";
import {
  formatShortCelebrationDateInput,
  isValidCalendarMMDDYY,
  expandShortDateToMMDDYYYY,
  getNextAnnualDate,
} from "../utils/celebrationDateInput.js";

const STEPS = {
  NAME: 0,
  RELATIONSHIP: 1,
  INTERESTS: 2,
  BUDGET: 3,
  OCCASIONS: 4,
  NOTES: 5,
  LOADING: 6,
};

const TOTAL_PROGRESS = 6;
const MIN_LOADING_MS = 1000;

const RELATIONSHIPS = [
  "Mom",
  "Dad",
  "Friend",
  "Partner / Spouse",
  "Child",
  "Sibling",
  "Brother",
  "Sister",
  "Coworker",
  "Grandparent",
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Keep the focused notes block centered in the visible area above the keyboard. */
function scrollNotesFieldIntoView(textarea) {
  const panel = textarea.closest("[data-notes-scroll-panel]");
  const block = textarea.closest("[data-notes-block]");
  if (!panel || !block) return;

  const align = () => {
    const vv = window.visualViewport;
    const visibleTop = vv?.offsetTop ?? 0;
    const visibleBottom = vv
      ? vv.offsetTop + vv.height
      : window.innerHeight;

    const footer = document.querySelector("[data-wizard-notes-footer]");
    const footerTop = footer?.getBoundingClientRect().top ?? visibleBottom;
    const safeBottom = Math.min(footerTop, visibleBottom) - 16;
    const safeTop = visibleTop + 16;

    if (safeBottom <= safeTop) return;

    const blockRect = block.getBoundingClientRect();
    const blockCenter = blockRect.top + blockRect.height / 2;
    const targetCenter = safeTop + (safeBottom - safeTop) / 2;
    const delta = blockCenter - targetCenter;

    if (Math.abs(delta) > 2) {
      panel.scrollTop += delta;
    }
  };

  requestAnimationFrame(align);
  window.setTimeout(align, 50);
  window.setTimeout(align, 150);
  window.setTimeout(align, 350);
}

export default function AddGifteeWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, checkUserHasGiftees } = useAuth();
  const [step, setStep] = useState(STEPS.NAME);
  const [saveError, setSaveError] = useState(null);
  const [isNameFieldFocused, setIsNameFieldFocused] = useState(false);
  const [isNotesFieldFocused, setIsNotesFieldFocused] = useState(false);

  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [relationship, setRelationship] = useState("");
  const [otherRelMode, setOtherRelMode] = useState(false);
  const [budgetMin, setBudgetMin] = useState(50);
  const [budgetMax, setBudgetMax] = useState(120);
  const [interests, setInterests] = useState(new Set());
  const [otherInterestMode, setOtherInterestMode] = useState(false);
  const [otherInterestText, setOtherInterestText] = useState("");
  const [occasions, setOccasions] = useState(new Set());
  const [birthdayDate, setBirthdayDate] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [customOccasions, setCustomOccasions] = useState([]);
  const [customOccMode, setCustomOccMode] = useState(false);
  const [customOccName, setCustomOccName] = useState("");
  const [customOccDate, setCustomOccDate] = useState("");
  const [customOccAnnual, setCustomOccAnnual] = useState(false);
  const [notesPrimary, setNotesPrimary] = useState("");
  const [notesAversions, setNotesAversions] = useState("");
  const [notesBonus, setNotesBonus] = useState("");
  const occasionList = getUsHolidayOccasions();

  const progress = Math.min(step + 1, TOTAL_PROGRESS);

  // Paint the brand gradient on html/body/#root while this route is open so the
  // strip behind Safari’s bottom chrome matches the wizard instead of flat
  // --gg-soft-sage from index.css.
  useEffect(() => {
    const nodes = [
      document.documentElement,
      document.body,
      document.getElementById("root"),
    ].filter(Boolean);
    const prev = nodes.map((el) => ({ el, background: el.style.background }));
    const bg =
      'var(--gg-soft-sage) url("/images/brand_gradient1.svg") center top / cover no-repeat';
    nodes.forEach((el) => {
      el.style.background = bg;
    });
    return () => {
      prev.forEach(({ el, background }) => {
        el.style.background = background;
      });
    };
  }, []);

  // Reset page scroll on every step change. iOS can scroll the document to keep
  // focused inputs visible (e.g. name step); the stale offset would push the
  // next step's content off-screen until the user manually scrolled back.
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsNotesFieldFocused(false);
  }, [step]);

  // On iOS Safari, position:fixed elements scroll with the page when the
  // keyboard is open. Name, relationship, and notes don't need body scroll
  // (content fits / notes scroll inside the step panel). Blocking touchmove is
  // the only reliable way to prevent the footer from drifting over content.
  // iOS's own focus-scroll (which runs before any touchmove) is unaffected.
  useEffect(() => {
    if (
      step !== STEPS.NAME &&
      step !== STEPS.RELATIONSHIP &&
      step !== STEPS.NOTES
    ) {
      return;
    }
    const prevent = (e) => {
      if (!(e.target instanceof Element)) return;
      // Let notes textareas and the notes step scroll panel move while the page stays locked.
      if (
        e.target.closest("textarea") ||
        e.target.closest("[data-notes-scroll-panel]")
      ) {
        return;
      }
      e.preventDefault();
    };
    document.addEventListener("touchmove", prevent, { passive: false });
    return () => document.removeEventListener("touchmove", prevent, { passive: false });
  }, [step]);

  // Notes focus: lock document scroll (footer drift) but keep the notes panel
  // aligned to the focused field as the keyboard animates open.
  useEffect(() => {
    if (!isNotesFieldFocused || step !== STEPS.NOTES) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    const pinWindowScroll = () => {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      body.scrollTop = 0;
    };

    const alignFocusedField = () => {
      pinWindowScroll();
      const active = document.activeElement;
      if (
        active instanceof HTMLTextAreaElement &&
        active.closest("[data-notes-scroll-panel]")
      ) {
        scrollNotesFieldIntoView(active);
      }
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    alignFocusedField();

    window.addEventListener("scroll", pinWindowScroll, true);
    window.visualViewport?.addEventListener("scroll", alignFocusedField);
    window.visualViewport?.addEventListener("resize", alignFocusedField);

    return () => {
      window.removeEventListener("scroll", pinWindowScroll, true);
      window.visualViewport?.removeEventListener("scroll", alignFocusedField);
      window.visualViewport?.removeEventListener("resize", alignFocusedField);
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [isNotesFieldFocused, step]);

  /* ── Navigation ──────────────────────────────────────────────────────── */

  const handleCloseWizard = () => {
    navigate(getWizardReturnPath(location), { replace: true });
  };

  const handleBack = () => {
    if (step === STEPS.INTERESTS && otherInterestMode) {
      setOtherInterestMode(false);
      setOtherInterestText("");
      return;
    }
    if (step === STEPS.OCCASIONS && customOccMode) {
      setCustomOccMode(false);
      setCustomOccName("");
      setCustomOccDate("");
      setCustomOccAnnual(false);
      return;
    }
    if (step === STEPS.NAME) {
      navigate(-1);
      return;
    }
    setStep((s) => s - 1);
  };

  const handleNext = async () => {
    if (step === STEPS.NOTES) {
      setStep(STEPS.LOADING);
      setSaveError(null);
      const loadingStartedAt = Date.now();
      try {
        const uid = currentUser?.uid;
        if (!uid) throw new Error("Not signed in");

        const gifteePayload = {
          userId: uid,
          name: name.trim(),
          gender,
          relationship,
          budgetMin,
          budgetMax,
          interests: [...interests],
          notesPrimary: notesPrimary.trim(),
          notesAversions: notesAversions.trim(),
          notesBonus: notesBonus.trim(),
          avatarUrl: null,
          reminderCount: 0,
          savedRecommendationCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        console.log("🔥 Attempting to create giftee document...");
        const gifteesCol = collection(db, "users", uid, "giftees");
        let gifteeDocRef;
        try {
          gifteeDocRef = await addDoc(gifteesCol, gifteePayload);
          console.log("✅ Giftee document created:", gifteeDocRef.id);
        } catch (error) {
          console.error("❌ Failed to create giftee document:", error);
          throw new Error(`Failed to create giftee: ${error.message}`);
        }

        console.log("🔥 Attempting to create reminder documents...");
        const remindersCol = collection(
          db, "users", uid, "giftees", gifteeDocRef.id, "reminders"
        );

        const selectedOccasions = [...occasions]
          .map((occId) => {
            const preset = occasionList.find((o) => o.id === occId);
            const custom = customOccasions.find((o) => o.id === occId);
            return preset || custom || null;
          })
          .filter(Boolean);

        if (isValidCalendarMMDDYY(birthdayDate)) {
          selectedOccasions.push({
            id: "birthday",
            name: "Birthday",
            date: birthdayDate,
            annual: true,
          });
        }

        if (isValidCalendarMMDDYY(anniversaryDate)) {
          selectedOccasions.push({
            id: "anniversary",
            name: "Anniversary",
            date: anniversaryDate,
            annual: true,
          });
        }

        let reminderCount = 0;
        for (const occ of selectedOccasions) {
          const custom = customOccasions.find((o) => o.id === occ.id);

          let reminderDate;
          if (occ.date && occ.date.includes("/")) {
            if (getNextUsHolidayOccurrence(occ.id)) {
              reminderDate = getNextUsHolidayOccurrence(occ.id);
            } else if (occ.annual !== false) {
              reminderDate = getNextAnnualDate(occ.date);
            } else {
              const parts = occ.date.split("/");
              const month = parseInt(parts[0], 10) - 1;
              const day = parseInt(parts[1], 10);
              const year = parts[2] ? parseInt(parts[2], 10) : new Date().getFullYear();
              reminderDate = new Date(year, month, day);
            }
          } else {
            reminderDate = new Date();
          }

          try {
            await addDoc(remindersCol, {
              userId: uid,
              gifteeId: gifteeDocRef.id,
              type: custom ? "custom" : occ.id,
              title: occ.name,
              date: Timestamp.fromDate(reminderDate),
              repeat: custom && !custom.annual ? "once" : "yearly",
              budget: null,
              notes: null,
              active: true,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            console.log(`✅ Reminder created: ${occ.name}`);
            reminderCount++;
          } catch (error) {
            console.error(`❌ Failed to create reminder for ${occ.name}:`, error);
            throw new Error(`Failed to create reminder: ${error.message}`);
          }
        }

        if (reminderCount > 0) {
          console.log("🔥 Attempting to update giftee reminder count...");
          try {
            const { updateDoc, doc: docRef } = await import("firebase/firestore");
            await updateDoc(
              docRef(db, "users", uid, "giftees", gifteeDocRef.id),
              { reminderCount }
            );
            console.log("✅ Giftee reminder count updated");
          } catch (error) {
            console.error("❌ Failed to update reminder count:", error);
            throw new Error(`Failed to update reminder count: ${error.message}`);
          }
        }

        await checkUserHasGiftees(uid);
        const elapsed = Date.now() - loadingStartedAt;
        if (elapsed < MIN_LOADING_MS) {
          await wait(MIN_LOADING_MS - elapsed);
        }
        navigate(
          getGifteeHubPath(gifteeDocRef.id),
          gifteeHubAfterCreateOptions(location),
        );
      } catch (error) {
        console.error("Error saving giftee:", error);
        setSaveError(error.message || "Failed to save. Please try again.");
        setStep(STEPS.NOTES);
      }
      return;
    }
    setStep((s) => s + 1);
  };

  /* ── Interest helpers ────────────────────────────────────────────────── */

  const toggleInterest = (label) => {
    setInterests((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleAddCustomInterest = () => {
    const trimmed = otherInterestText.trim();
    if (!trimmed) return;
    setInterests((prev) => new Set([...prev, trimmed]));
    setOtherInterestText("");
    setOtherInterestMode(false);
  };

  /* ── Occasion helpers ────────────────────────────────────────────────── */

  const toggleOccasion = (id) => {
    setOccasions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddCustomOccasion = () => {
    const trimmed = customOccName.trim();
    if (!trimmed || !isValidCalendarMMDDYY(customOccDate)) return;
    const newOcc = {
      id: `custom-${Date.now()}`,
      name: trimmed,
      date: expandShortDateToMMDDYYYY(customOccDate),
      annual: customOccAnnual,
    };
    setCustomOccasions((prev) => [...prev, newOcc]);
    setOccasions((prev) => new Set([...prev, newOcc.id]));
    setCustomOccName("");
    setCustomOccDate("");
    setCustomOccAnnual(false);
    setCustomOccMode(false);
  };

  const removeCustomOccasion = (id) => {
    setCustomOccasions((prev) => prev.filter((occ) => occ.id !== id));
    setOccasions((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  /* ── Budget helpers ──────────────────────────────────────────────────── */

  const handleBudgetMin = (input) => {
    const rawValue = typeof input === "number" ? input : Number(input.target.value);
    const val = Math.min(Math.max(rawValue, 10), budgetMax - 10);
    setBudgetMin(val);
  };

  const handleBudgetMax = (input) => {
    const rawValue = typeof input === "number" ? input : Number(input.target.value);
    const val = Math.max(Math.min(rawValue, 200), budgetMin + 10);
    setBudgetMax(val);
  };

  const fmtBudget = (v) => (v >= 200 ? "$200+" : `$${v}`);

  /* ── Button config ───────────────────────────────────────────────────── */

  const getButtonConfig = () => {
    const hasSelectedCelebration =
      occasions.size > 0 ||
      isValidCalendarMMDDYY(birthdayDate) ||
      isValidCalendarMMDDYY(anniversaryDate);

    if (step === STEPS.OCCASIONS && customOccMode) {
      const nameOk = Boolean(customOccName.trim());
      const dateOk = isValidCalendarMMDDYY(customOccDate);
      return {
        text: "Add",
        icon: "fa-solid fa-plus",
        disabled: !nameOk || !dateOk,
        onClick: handleAddCustomOccasion,
      };
    }
    switch (step) {
      case STEPS.NAME:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: !name.trim() || !gender,
          onClick: handleNext,
        };
      case STEPS.RELATIONSHIP:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: !relationship,
          onClick: handleNext,
        };
      case STEPS.INTERESTS:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: interests.size === 0,
          onClick: handleNext,
        };
      case STEPS.BUDGET:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: false,
          onClick: handleNext,
        };
      case STEPS.OCCASIONS:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: !hasSelectedCelebration,
          onClick: handleNext,
        };
      case STEPS.NOTES:
        return {
          text: "Next",
          icon: "fa-solid fa-arrow-right",
          disabled: false,
          onClick: handleNext,
        };
      default:
        return null;
    }
  };

  /* ══════════════════════════════════════════════════════════════════════
     LOADING SCREEN
     ══════════════════════════════════════════════════════════════════════ */

  if (step === STEPS.LOADING) {
    return (
      <div className={`${styles.wizard} ${styles.loadingScreen}`}>
        <div className={styles.loadingInner}>
          <div className={styles.loadingBody} aria-live="polite" aria-busy="true">
            <img
              src="/images/GiftGaff_Logo.svg"
              alt=""
              className={styles.loadingIcon}
            />
            <div className={styles.loadingCopy}>
              <p className={styles.loadingEyebrow}>Hang tight</p>
              <p className={styles.loadingText}>
                Gift Gaff is generating gift recommendations for {name || "them"}...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════════
     WIZARD SHELL
     ══════════════════════════════════════════════════════════════════════ */

  const btn = getButtonConfig();
  const isNameStep = step === STEPS.NAME;
  const isRelationshipStep = step === STEPS.RELATIONSHIP;
  const isInterestsStep = step === STEPS.INTERESTS;
  const isBudgetStep = step === STEPS.BUDGET;
  const isOccasionsStep = step === STEPS.OCCASIONS;
  const isNotesStep = step === STEPS.NOTES;
  const isFigmaWizardStep =
    isNameStep ||
    isRelationshipStep ||
    isInterestsStep ||
    isBudgetStep ||
    isOccasionsStep ||
    isNotesStep;
  const canSaveCustomOccasion =
    Boolean(customOccName.trim()) && isValidCalendarMMDDYY(customOccDate);
  const footerStepLabel =
    step === STEPS.RELATIONSHIP
      ? "Relationship"
      : step === STEPS.INTERESTS
        ? "Interests"
        : step === STEPS.BUDGET
          ? "Budget"
          : step === STEPS.OCCASIONS
            ? "Celebrations"
              : step === STEPS.NOTES
                ? "Notes"
                : "";
  const footerSegmentCount = isNotesStep ? 5 : 6;

  return (
    <div
      className={`${styles.wizard} ${
        isFigmaWizardStep ? styles.wizardNameStep : ""
      }`}
    >
      <div className={`${styles.wizardInner} ${
        (isNameStep || isRelationshipStep || isOccasionsStep || isNotesStep)
          ? styles.wizardInnerLocked : ""
      } ${isInterestsStep ? styles.wizardInnerInterests : ""} ${
        isBudgetStep ? styles.wizardInnerBudget : ""
      } ${isOccasionsStep ? styles.wizardInnerCelebrations : ""
      }`}>
        {isFigmaWizardStep ? (
          <div className={styles.nameStepHeader}>
            <h1 className={styles.nameStepTitle}>New Giftee</h1>
            <button
              type="button"
              className={styles.nameStepCloseBtn}
              onClick={handleCloseWizard}
              aria-label="Close add giftee wizard"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className={styles.header}>
              <button type="button" className={styles.headerBtn} aria-label="Menu">
                <i className="fa-solid fa-bars" />
              </button>
              <img
                src="/images/navbar-logo.svg"
                alt="Gift Gaff"
                className={styles.headerLogo}
              />
            </div>

            {/* Sub-header */}
            <div className={styles.subHeader}>
              <button type="button" className={styles.backBtn} onClick={handleBack}>
                <i className="fa-solid fa-arrow-left" />
                <span>Back</span>
              </button>
              <h1 className={styles.subTitle}>Add Giftee</h1>
            </div>

            {/* Progress */}
            <div className={styles.progress}>
              {Array.from({ length: TOTAL_PROGRESS }, (_, i) => (
                <div
                  key={i}
                  className={`${styles.progressSeg} ${
                    i < progress ? styles.progressSegActive : ""
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Content */}
        <div
          className={`${styles.content} ${
            isRelationshipStep
              ? styles.relationshipStepContent
              : ""
          } ${isInterestsStep ? styles.interestsStepContentOuter : ""} ${
            isBudgetStep ? styles.budgetStepContent : ""
          } ${
            isOccasionsStep ? styles.celebrationsStepContentOuter : ""
          } ${
            isNotesStep ? styles.notesStepContent : ""
          } ${
            isNotesStep && isNotesFieldFocused ? styles.notesStepContentFocused : ""
          } ${
          step === STEPS.OCCASIONS ? styles.contentFixed : ""
          }`}
          {...(isNotesStep ? { "data-notes-scroll-panel": true } : {})}
        >
          {step === STEPS.NAME && (
            <NameStep
              name={name}
              setName={setName}
              gender={gender}
              setGender={setGender}
              setIsNameFieldFocused={setIsNameFieldFocused}
            />
          )}
          {step === STEPS.RELATIONSHIP && (
            <RelationshipStep
              name={name}
              relationship={relationship}
              setRelationship={setRelationship}
              otherRelMode={otherRelMode}
              setOtherRelMode={setOtherRelMode}
            />
          )}
          {step === STEPS.INTERESTS && (
            <InterestsStep
              name={name}
              interests={interests}
              onToggle={toggleInterest}
              onCustomInterest={() => setOtherInterestMode(true)}
            />
          )}
          {step === STEPS.BUDGET && (
            <BudgetStep
              name={name}
              budgetMin={budgetMin}
              budgetMax={budgetMax}
              onMinChange={handleBudgetMin}
              onMaxChange={handleBudgetMax}
              fmtBudget={fmtBudget}
            />
          )}
          {step === STEPS.OCCASIONS && (
            <OccasionsStep
              name={name}
              occasions={occasions}
              birthdayDate={birthdayDate}
              setBirthdayDate={setBirthdayDate}
              anniversaryDate={anniversaryDate}
              setAnniversaryDate={setAnniversaryDate}
              customOccasions={customOccasions}
              occasionList={occasionList}
              onToggle={toggleOccasion}
              onRemoveCustom={removeCustomOccasion}
              onCustomMode={() => setCustomOccMode(true)}
            />
          )}
          {step === STEPS.NOTES && (
            <>
              {saveError && (
                <p style={{ color: "red", fontSize: 14, marginBottom: 8 }}>
                  {saveError}
                </p>
              )}
              <NotesStep
                name={name}
                gender={gender}
                notesPrimary={notesPrimary}
                setNotesPrimary={setNotesPrimary}
                notesAversions={notesAversions}
                setNotesAversions={setNotesAversions}
                notesBonus={notesBonus}
                setNotesBonus={setNotesBonus}
                setIsNotesFieldFocused={setIsNotesFieldFocused}
              />
            </>
          )}
        </div>

        {/* Bottom button */}
        {btn && !isFigmaWizardStep && (
          <div className={styles.bottomBar}>
            <button
              type="button"
              className={`${styles.bottomBtn} ${
                btn.disabled ? styles.bottomBtnDisabled : ""
              }`}
              disabled={btn.disabled}
              onClick={btn.onClick}
            >
              <span>{btn.text}</span>
              {btn.icon && <i className={btn.icon} />}
            </button>
          </div>
        )}
        {customOccMode && (
          <CustomOccasionEntry
            customOccName={customOccName}
            setCustomOccName={setCustomOccName}
            customOccDate={customOccDate}
            setCustomOccDate={setCustomOccDate}
            customOccAnnual={customOccAnnual}
            setCustomOccAnnual={setCustomOccAnnual}
            canSave={canSaveCustomOccasion}
            onClose={handleBack}
            onSave={handleAddCustomOccasion}
          />
        )}
        {(isRelationshipStep ||
          isInterestsStep ||
          isBudgetStep ||
          isOccasionsStep ||
          isNotesStep) && btn && !customOccMode && (
          <div
            className={`${styles.relationshipFooter} ${
              isInterestsStep || isOccasionsStep
                ? styles.relationshipFooterFrosted
                : ""
            } ${
              isBudgetStep ||
              isOccasionsStep ||
              (isNotesStep && isNotesFieldFocused)
                ? styles.relationshipFooterUnderKeyboard
                : ""
            } ${
              isNotesStep && isNotesFieldFocused
                ? styles.relationshipFooterFocused
                : ""
            }`}
            {...(isNotesStep ? { "data-wizard-notes-footer": true } : {})}
          >
            <div className={styles.relationshipFooterInner}>
              <div className={styles.relationshipControlsRow}>
                <button
                  type="button"
                  className={styles.relationshipBackBtn}
                  onMouseDown={(e) => {
                    if (isNotesFieldFocused) e.preventDefault();
                  }}
                  onClick={handleBack}
                  aria-label="Back"
                >
                  <i className="fa-solid fa-arrow-left" />
                </button>
                <button
                  type="button"
                  className={`${styles.relationshipNextBtn} ${
                    btn.disabled ? styles.bottomBtnDisabled : ""
                  }`}
                  disabled={btn.disabled}
                  onMouseDown={(e) => {
                    if (isNotesFieldFocused) e.preventDefault();
                  }}
                  onClick={btn.onClick}
                >
                  <span>{btn.text}</span>
                  {btn.icon && <i className={btn.icon} />}
                </button>
              </div>
              {!(isNotesStep && isNotesFieldFocused) && (
                <div className={styles.relationshipProgressCard}>
                  <div className={styles.relationshipProgressLabels}>
                    <span>{progress} / 6</span>
                    <span>{footerStepLabel}</span>
                  </div>
                  <div className={styles.relationshipProgressTrack}>
                    {Array.from({ length: footerSegmentCount }, (_, i) => (
                      <div
                        key={i}
                        className={`${styles.relationshipProgressSeg} ${
                          i < progress ? styles.relationshipProgressSegActive : ""
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isNameStep && (
          <div
            className={`${styles.nameStepFooter} ${
              isNameFieldFocused ? styles.nameStepFooterFocused : ""
            }`}
          >
            <div className={styles.nameStepFooterInner}>
              <div className={styles.nameStepNextRow}>
                <button
                  type="button"
                  className={styles.nameStepTypingNextBtn}
                  disabled={!name.trim() || !gender}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleNext}
                  aria-label="Next"
                >
                  <span>Next</span>
                  <i className="fa-solid fa-arrow-right" />
                </button>
              </div>
              {!isNameFieldFocused && (
                <div className={styles.nameStepProgressCard}>
                  <div className={styles.nameStepProgressLabels}>
                    <span>1 / 6</span>
                    <span>Name</span>
                  </div>
                  <div className={styles.nameStepProgressTrack}>
                    {Array.from({ length: 6 }, (_, i) => (
                      <div
                        key={i}
                        className={`${styles.nameStepProgressSeg} ${
                          i < 1 ? styles.nameStepProgressSegActive : ""
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isInterestsStep && otherInterestMode && (
          <CustomInterestDrawer
            otherInterestText={otherInterestText}
            setOtherInterestText={setOtherInterestText}
            onClose={() => {
              setOtherInterestMode(false);
              setOtherInterestText("");
            }}
            onSave={handleAddCustomInterest}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Step sub-components (file-scoped)
   ═══════════════════════════════════════════════════════════════════════════ */

function NameStep({ name, setName, gender, setGender, setIsNameFieldFocused }) {
  return (
    <>
      <p className={styles.question}>
        Who are you shopping for?
      </p>
      <div className={styles.nameFieldsStack}>
        <div className={styles.nameInputWrap}>
          <input
            type="text"
            className={styles.nameInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setIsNameFieldFocused(true)}
            onBlur={() => setIsNameFieldFocused(false)}
            placeholder="First Name"
          />
        </div>
        <div className={styles.nameGenderSelectWrap}>
          <select
            className={`${styles.nameGenderSelect} ${
              gender ? "" : styles.nameGenderSelectEmpty
            }`}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            aria-label="Gender"
          >
            <option value="" disabled>
              Gender
            </option>
            {GENDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <i
            className={`fa-solid fa-chevron-down ${styles.nameGenderSelectChevron}`}
            aria-hidden
          />
        </div>
      </div>
    </>
  );
}

function RelationshipStep({
  name,
  relationship,
  setRelationship,
  otherRelMode,
  setOtherRelMode,
}) {
  const [otherRelEditing, setOtherRelEditing] = useState(false);

  const orderedPresets =
    relationship && RELATIONSHIPS.includes(relationship) && !otherRelMode
      ? [
          relationship,
          ...RELATIONSHIPS.filter((rel) => rel !== relationship),
        ]
      : RELATIONSHIPS;

  const selectPreset = (rel) => {
    setRelationship(rel);
    setOtherRelMode(false);
    setOtherRelEditing(false);
  };

  const keepInputFocusForChipSelect = (event) => {
    // Prevent blur-before-click so the custom input doesn't flash committed styles
    // while switching to a preset chip.
    event.preventDefault();
  };

  return (
    <>
      <p className={styles.question}>
        What is your relation to {name || "them"}?
      </p>
      <div className={styles.relationshipChipGrid}>
        {orderedPresets.map((rel) => (
          <button
            key={rel}
            type="button"
            className={`${styles.relationshipChip} ${
              relationship === rel && !otherRelMode ? styles.relationshipChipSelected : ""
            }`}
            onPointerDown={keepInputFocusForChipSelect}
            onClick={() => selectPreset(rel)}
          >
            {rel}
          </button>
        ))}
        <button
          type="button"
          className={`${styles.relationshipChip} ${styles.relationshipChipCustom} ${
            otherRelMode ? styles.relationshipChipSelected : ""
          }`}
          onClick={() => {
            setOtherRelMode(true);
            setOtherRelEditing(true);
            setRelationship("");
          }}
        >
          <i className="fa-solid fa-plus" />
          <span>Custom</span>
        </button>
      </div>
      {otherRelMode && (
        <div className={styles.otherRelWrap}>
          <input
            type="text"
            className={`${styles.otherRelInput} ${
              otherRelEditing ? styles.otherRelInputTyping : ""
            }`}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            onFocus={() => setOtherRelEditing(true)}
            onBlur={() => setOtherRelEditing(false)}
            placeholder="Enter relationship"
            autoFocus
          />
        </div>
      )}
    </>
  );
}

function BudgetStep({ name, budgetMin, budgetMax, onMinChange, onMaxChange, fmtBudget }) {
  return (
    <div className={styles.budgetStep}>
      <p className={styles.budgetQuestion}>
        How much do you want to spend
        <br />
        on {name || "them"}? (Per Gift)
      </p>
      <GifteeBudgetRangeSlider
        budgetMin={budgetMin}
        budgetMax={budgetMax}
        onMinChange={onMinChange}
        onMaxChange={onMaxChange}
        fmtBudget={fmtBudget}
      />
    </div>
  );
}

function InterestsStep({ name, interests, onToggle, onCustomInterest }) {
  const customAdded = customInterestsFromSet(interests);

  return (
    <div className={styles.interestsScroll}>
      <p className={styles.interestsQuestion}>
        What sort of things is
        <br />
        {name || "they"} into?
      </p>
      <div className={styles.interestsOrderedList}>
        <div className={styles.interestsSection}>
          <p className={styles.interestsSectionLabel}>Custom Interests</p>
          <div className={styles.interestsChipRow}>
            <button
              type="button"
              className={styles.interestChipCustomAdd}
              onClick={onCustomInterest}
            >
              <i className={`fa-solid fa-plus ${styles.interestChipCustomAddIcon}`} aria-hidden />
              Custom Interest
            </button>
            {customAdded.map((label) => (
              <button
                key={`custom-${label}`}
                type="button"
                className={styles.interestChipUserCustom}
                onClick={() => onToggle(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {INTEREST_GROUPS.map((group) => (
          <div key={group.id} className={styles.interestsSection}>
            <p className={styles.interestsSectionLabel}>{group.label}</p>
            <div className={styles.interestsChipRow}>
              {group.chips.map((chip) => {
                const selected = interests.has(chip);
                return (
                  <button
                    key={chip}
                    type="button"
                    className={`${styles.interestChipPreset} ${
                      selected ? styles.interestChipPresetSelected : ""
                    }`}
                    onClick={() => onToggle(chip)}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomInterestDrawer({
  otherInterestText,
  setOtherInterestText,
  onClose,
  onSave,
}) {
  const hasInterestText = otherInterestText.trim().length > 0;
  return (
    <div className={styles.customInterestDrawer} role="dialog" aria-modal="true" aria-labelledby="custom-interest-title">
      <button
        type="button"
        className={styles.customInterestCloseBtn}
        onClick={onClose}
        aria-label="Close custom interest"
      >
        <i className="fa-solid fa-xmark" />
      </button>
      <div className={styles.customInterestDrawerBody}>
        <p id="custom-interest-title" className={styles.customInterestTitle}>
          Add Custom Interest
        </p>
        <input
          type="text"
          className={styles.customInterestInput}
          value={otherInterestText}
          onChange={(e) => setOtherInterestText(e.target.value)}
          placeholder="Interest"
          onKeyDown={(e) => {
            if (e.key === "Enter" && hasInterestText) onSave();
            if (e.key === "Escape") onClose();
          }}
          autoFocus
        />
      </div>
      {hasInterestText && (
        <div className={styles.customInterestActions}>
          <button
            type="button"
            className={styles.customInterestSaveBtn}
            onClick={onSave}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}

function OccasionsStep({
  name,
  occasions,
  birthdayDate,
  setBirthdayDate,
  anniversaryDate,
  setAnniversaryDate,
  customOccasions,
  occasionList,
  onToggle,
  onRemoveCustom,
  onCustomMode,
}) {
  return (
    <div className={styles.celebrationsScroll}>
      <p className={styles.celebrationsQuestion}>
        When do you want to celebrate {name || "them"}?
      </p>
      <div className={styles.celebrationsOrderedList}>
        <div className={styles.celebrationSection}>
          <label className={styles.celebrationSectionLabel} htmlFor="birthday-date">
            Birthday
          </label>
          <input
            id="birthday-date"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={styles.celebrationDateInput}
            value={birthdayDate}
            onChange={(e) => setBirthdayDate(formatShortCelebrationDateInput(e.target.value))}
            placeholder="MM/DD/YY"
            aria-label="Birthday date"
          />
        </div>
        <div className={styles.celebrationSection}>
          <label className={styles.celebrationSectionLabel} htmlFor="anniversary-date">
            Anniversary
          </label>
          <input
            id="anniversary-date"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={styles.celebrationDateInput}
            value={anniversaryDate}
            onChange={(e) => setAnniversaryDate(formatShortCelebrationDateInput(e.target.value))}
            placeholder="MM/DD/YY"
            aria-label="Anniversary date"
          />
        </div>
        <div className={styles.celebrationSection}>
          <p className={styles.celebrationSectionLabel}>
            Custom Celebrations(Birthdays, Graduations, etc.)
          </p>
          <div className={styles.customCelebrationStack}>
            {customOccasions.map((occ) => (
              <button
                key={occ.id}
                type="button"
                className={`${styles.customCelebrationChip} ${styles.customCelebrationChipSelected}`}
                onClick={() => onRemoveCustom(occ.id)}
                aria-label={`Remove custom celebration ${occ.name}`}
              >
                <span>{occ.name}</span>
                {occ.date && (
                  <span className={styles.celebrationChipDate}>{occ.date.slice(0, 5)}</span>
                )}
              </button>
            ))}
            <button
              type="button"
              className={styles.customCelebrationAddChip}
              onClick={onCustomMode}
            >
              <i className="fa-solid fa-plus" aria-hidden />
              <span>Custom celebration</span>
            </button>
          </div>
        </div>
        <div className={styles.celebrationSection}>
          <p className={styles.celebrationSectionLabel}>U.S. Holidays</p>
          <div className={styles.holidayCelebrationStack}>
            {occasionList.map((occ) => (
              <button
                key={occ.id}
                type="button"
                className={`${styles.holidayCelebrationChip} ${
                  occasions.has(occ.id) ? styles.holidayCelebrationChipSelected : ""
                }`}
                onClick={() => onToggle(occ.id)}
              >
                <span>{occ.name}</span>
                <span className={styles.celebrationChipDate}>{occ.date}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomOccasionEntry({
  customOccName,
  setCustomOccName,
  customOccDate,
  setCustomOccDate,
  customOccAnnual,
  setCustomOccAnnual,
  canSave,
  onClose,
  onSave,
}) {
  return (
    <div className={styles.customOccDrawer} role="dialog" aria-modal="true" aria-labelledby="custom-occasion-title">
      <button
        type="button"
        className={styles.customOccCloseBtn}
        onClick={onClose}
        aria-label="Close custom celebration"
      >
        <i className="fa-solid fa-xmark" />
      </button>
      <div className={styles.customOccDrawerBody}>
        <p id="custom-occasion-title" className={styles.customOccTitle}>
          Custom Celebration
        </p>
        <input
          type="text"
          className={styles.customOccNameInput}
          value={customOccName}
          onChange={(e) => setCustomOccName(e.target.value)}
          placeholder="Title"
          autoFocus
        />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={styles.customOccDateInput}
          value={customOccDate}
          onChange={(e) => setCustomOccDate(formatShortCelebrationDateInput(e.target.value))}
          placeholder="MM/DD/YY"
          aria-label="Custom celebration date"
        />
      </div>
      <div className={styles.customOccDrawerControls}>
        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Annual reminder</span>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={customOccAnnual}
              onChange={(e) => setCustomOccAnnual(e.target.checked)}
            />
            <span className={styles.toggleTrack}>
              <span className={styles.toggleText}>
                {customOccAnnual ? "Yes" : "No"}
              </span>
            </span>
          </label>
        </div>
        {canSave && (
          <button
            type="button"
            className={styles.customOccSaveBtn}
            onClick={onSave}
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
}

function NotesStep({
  name,
  gender,
  notesPrimary,
  setNotesPrimary,
  notesAversions,
  setNotesAversions,
  notesBonus,
  setNotesBonus,
  setIsNotesFieldFocused,
}) {
  const displayName = name.trim() || "them";
  const pronoun = subjectPronoun(gender);
  const reflexive = reflexivePronoun(gender);

  const handleFocus = (event) => {
    setIsNotesFieldFocused(true);
    scrollNotesFieldIntoView(event.currentTarget);
  };

  const handleBlur = () => setIsNotesFieldFocused(false);

  return (
    <div className={styles.notesStep}>
      <div className={styles.notesBlock} data-notes-block>
        <p className={styles.notesBlockLabel}>1. Primary</p>
        <p className={styles.notesBlockPrompt}>
          Tell us a bit about {displayName} — what does {pronoun} enjoy doing on
          a typical week? What&apos;s {pronoun} like?
        </p>
        <textarea
          className={styles.notesBlockArea}
          rows={1}
          value={notesPrimary}
          onChange={(e) => setNotesPrimary(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type here"
          aria-label={`Primary notes about ${displayName}`}
        />
      </div>

      <div className={styles.notesBlock} data-notes-block>
        <p className={styles.notesBlockLabel}>2. Aversions</p>
        <p className={styles.notesBlockPrompt}>
          Anything to avoid? (allergies, scents, etc.)
        </p>
        <textarea
          className={styles.notesBlockArea}
          rows={1}
          value={notesAversions}
          onChange={(e) => setNotesAversions(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type here"
          aria-label={`Aversions for ${displayName}`}
        />
      </div>

      <div className={styles.notesBlock} data-notes-block>
        <p className={styles.notesBlockLabel}>3. Bonus</p>
        <p className={styles.notesBlockPrompt}>
          Something {displayName} would love but probably wouldn&apos;t buy for{" "}
          {reflexive}?
        </p>
        <textarea
          className={styles.notesBlockArea}
          rows={1}
          value={notesBonus}
          onChange={(e) => setNotesBonus(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type here"
          aria-label={`Bonus gift ideas for ${displayName}`}
        />
      </div>
    </div>
  );
}
